/**
 * @typedef {object} SignedFileResource
 * @property {string} uuid Item's id
 * @property {number} revision Item's revision number
 * @property {string} digest_sha256 Checksum
 * @property {string} name File name
 * @property {string} parent_uuid Folder ID
 * @property {string} file_modification_date Last modification date
 * @property {?Array.<SignedDocumentResource>} documents List of the documents
 */

/**
 * @typedef {object} SignedDocumentResource
 * @property {string} uuid Document ID
 * @property {string} node_uuid File's ID
 * @property {number} node_revision Revision number
 * @property {('initiated'|'waiting'|'signed'|'notarized'|'terminated')} status Document's status
 * @property {?string} signed_at Signing completion date
 * @property {boolean} embedded True if file was signed with embedded signature
 * @property {boolean} has_links True if the document has been shared (has shared links)
 * @property {SigneeDocumentPermissions} permissions List of permissions for the document
 * @property {string} notary_certificate_transaction_id Certificate transaction ID
 * @property {string} notary_signed_file_name Signed file name from notary service (?)
 * @property {Array.<SigneeResource>} signees List of signees
 */

/**
 * @typedef {object} SigneeResource
 * @property {string} uuid Signee UUID
 * @property {string} email Email
 * @property {?string} signed_at Signing date or null whether not signed yet
 * @property {boolean} is_owner Is the owner of the file
 */

/**
 * @typedef {object} SigneeDocumentPermissions
 * @property {boolean} can_be_deleted True if the signed document could be deleted
 */

angular.module('access.Nodes')
    .factory('Esignature', ['$resource', 'EsignatureDocumentInterceptor', 'apiAddressFilter',
        function ($resource, EsignatureDocumentInterceptor, apiAddressFilter) {
            var EsignatureResource = $resource('/sync_and_share_nodes/:node_uuid/esignatures/:action',
                {
                    node_uuid: '@node_uuid'
                },
                {
                    /**
                     * Initialize signature document
                     * GET /sync_and_share_nodes/[node_uuid]/esignatures/settings
                     * {
                     *   node_uuid,
                     *   node_revision,
                     *   node_name,
                     *   node_size,
                     *   node_digest_sha,
                     *   node_supports_conversion_to_pdf,
                     *   notary_pdf_annotation_url
                     * }
                     */
                    getSettings: {
                        method: 'GET',
                        params: {
                            action: 'settings'
                        }
                    },
                    /**
                     * Return all signatures documents for a node (ASRV-10094):
                     * GET /sync_and_share_nodes/[node_uuid]/esignatures
                     * [
                     *   {
                     *     uuid,
                     *     node_uuid,
                     *     node_revision,
                     *     status,
                     *     signed_at,
                     *     signees: [
                     *       {
                     *         uuid,
                     *         email,
                     *         signed_at,
                     *         is_owner
                     *       },
                     *       ...
                     *     ]
                     *   },
                     *   ...
                     * ]
                     */
                    getDocuments: {
                        interceptor: EsignatureDocumentInterceptor,
                        isArray: true,
                        method: 'GET'
                    },
                    /**
                     * Return a signatures document by ID (ASRV-10167):
                     * GET /sync_and_share_nodes/[node_uuid]/esignatures/[uuid]
                     * {
                     *   uuid,
                     *   node_uuid,
                     *   node_revision,
                     *   status,
                     *   signed_at,
                     *   signees: [
                     *     {
                     *       uuid,
                     *       email,
                     *       signed_at,
                     *       is_owner
                     *     },
                     *     ...
                     *   ],
                     *   notarization_certificate_url,
                     *   signature_certificate_download_url
                     * }
                     */
                    getDocument: {
                        interceptor: EsignatureDocumentInterceptor,
                        method: 'GET',
                        params: {
                            action: '@uuid'
                        }
                    },
                    getEmbeddedFile: {
                        method: 'GET',
                        params: {
                            action: 'embedded_signatures_file'
                        },
                        ignoreApiErrors: true,
                        responseType: 'arraybuffer',
                        transformResponse: function (data, _, status) {
                            if (status >= 400) {
                                if (angular.isFunction(window.TextDecoder)) { // IE does not support TextDecoder, so we'll fail silently
                                    // decode JSON from ArrayBuffer
                                    var decoder = new TextDecoder('utf-8');

                                    return angular.fromJson(decoder.decode(data));
                                }
                            } else {
                                return { content: data };
                            }
                        }
                    },
                    resend: {
                        method: 'POST',
                        url: apiAddressFilter('/sync_and_share_nodes/:node_uuid/esignatures/:uuid/resend_invitation'),
                        params: {
                            uuid: '@uuid',
                            node_uuid: '@node_uuid'
                        }
                    }
                }
            );
            /**
             * Return all node revisions with associated signatures document (ASRV-10097):
             * GET /sync_and_share_nodes/signed_file_revisions
             * [
             *   {
             *     name,
             *     uuid,
             *     revision
             *   },
             *   ...
             * ]
             */
            var EsignatureRevisionsResource = $resource('/sync_and_share_nodes/signed_file_revisions');
            EsignatureResource.query = EsignatureRevisionsResource.query.bind(EsignatureResource);

            EsignatureRevisionsResource.getDownloadUrl = function (esignatureRevision) {
                return esignatureRevision.actions.download;
            };

            return EsignatureResource;
        }]);
